import axios from 'axios';

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Fetch user's geolocation data
 * @returns {Promise<Object|null>} Geolocation data or null if failed
 */
export const getGeoLocation = async () => {
  try {
    const response = await axios.get(GEOLOCATION_API, {
      headers: {
        'accept': 'text/plain'
      }
    });
    
    if (response.data && response.data.isSuccess) {
      return response.data;
    }
    console.warn('Geolocation API returned unsuccessful response');
    return null;
  } catch (error) {
    console.error('Geolocation API error:', error);
    return null;
  }
};

/**
 * Submit lead form data to API
 * @param {Object} formData - Form data containing name, email, phone, description
 * @param {Object|null} geoLocationData - Geolocation data from getGeoLocation()
 * @param {string} packageTitle - Optional package title
 * @param {number} packageAmount - Optional package amount
 * @returns {Promise<Object>} Result object with success status and data/error
 */
export const submitLead = async (formData, geoLocationData = null, packageTitle = '', packageAmount = 0) => {
  try {
    // Extract only numeric digits from phone (remove +, spaces, dashes, etc.)
    const phoneNumber = formData.phone.replace(/\D/g, '');
    
    // Parse country dial code to integer
    const countryDialCode = parseInt(formData.countryCode.replace('+', ''));
    
    // Extract data from geolocation
    const ipAddress = geoLocationData?.data?.ipAddress || '0.0.0.0';
    const country = geoLocationData?.data?.countryCode || '';
    const city = geoLocationData?.data?.cityName || '';
    const state = geoLocationData?.data?.regionName || '';
    
    // Prepare payload according to API specification
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: countryDialCode,
      country: country,
      phoneNumber: phoneNumber,
      ipAddress: ipAddress,
      description: formData.description || '',
      pageUrl: window.location.href,
      city: city,
      state: state,
      geoLocation: geoLocationData ? JSON.stringify(geoLocationData) : '',
      packageTitle: packageTitle,
      packageAmount: packageAmount
    };

    console.log('Submitting lead with payload:', payload);

    const response = await axios.post(LEAD_API, payload, {
      headers: {
        'Content-Type': 'application/json'
      }
    });

    // Success case (HTTP 200)
    if (response.status === 200 && response.data.isSuccess) {
      return {
        success: true,
        statusCode: 200,
        customerId: response.data.data?.customerId,
        data: response.data
      };
    }

    // Handle unexpected success response format
    return {
      success: false,
      statusCode: response.status,
      errorMessage: 'Unexpected response format from server'
    };

  } catch (error) {
    console.error('Lead API error:', error);

    if (error.response) {
      const statusCode = error.response.status;
      const responseData = error.response.data;

      // HTTP 401 - Unauthorized (domain not configured)
      if (statusCode === 401) {
        return {
          success: false,
          statusCode: 401,
          errorMessage: responseData.message || 'Unauthorized access'
        };
      } 
      
      // HTTP 400 - Bad Request (validation error)
      if (statusCode === 400) {
        return {
          success: false,
          statusCode: 400,
          errorMessage: responseData.errorMessage || responseData.data?.message || 'Invalid form data'
        };
      }

      // Other HTTP errors
      return {
        success: false,
        statusCode: statusCode,
        errorMessage: responseData.message || responseData.errorMessage || 'Server error occurred'
      };
    }

    // Network or other errors
    return {
      success: false,
      statusCode: 500,
      errorMessage: 'Network error. Please check your connection and try again.'
    };
  }
};

/**
 * Complete form submission handler with redirect logic
 * @param {Object} formData - Form data
 * @param {Object|null} geoLocationData - Geolocation data
 * @param {Function} onError - Error callback function
 * @param {string} packageTitle - Optional package title
 * @param {number} packageAmount - Optional package amount
 */
export const handleFormSubmission = async (formData, geoLocationData, onError, packageTitle = '', packageAmount = 0) => {
  const result = await submitLead(formData, geoLocationData, packageTitle, packageAmount);

  if (result.success && result.statusCode === 200) {
    // Success - redirect based on customerId availability
    if (result.customerId) {
      // If customerId exists, redirect to application page
      window.location.href = `/application/main?customerid=${result.customerId}`;
    } else {
      // If no customerId, redirect to thank you page
      window.location.href = '/thank-you';
    }
  } else {
    // Error - show error message to user
    onError(result.errorMessage || 'An error occurred. Please try again.');
  }
};
